import {type RowKey, useBoardRow} from '@github-ui/board-core'
import {clsx} from 'clsx'
import type {ReactNode} from 'react'

import styles from './BoardRowFrame.module.css'

export type BoardRowFrameProps = {
  rowKey: RowKey
  /**
   * The row's header. Typically a `BoardRowHeader`.
   *
   * When it is absent the row draws no chrome and claims no accessible name, so a board
   * with no swimlane reads exactly as it did before swimlanes existed. That is a
   * decision about whether the caller asked for row headers, not about how many rows
   * the board has. A genuinely swimlaned board whose second row has not paged in yet
   * keeps its headers.
   */
  header?: ReactNode
  /** The row's strip of cells. */
  children?: ReactNode
  className?: string
}

/**
 * One swimlane: its header, and the strip of cells beneath it.
 *
 * A board with no swimlane has exactly one row, so this renders for every board and
 * nothing anywhere asks whether the board is swimlaned.
 */
export function BoardRowFrame({rowKey, header, children, className}: BoardRowFrameProps) {
  const {rowProps} = useBoardRow(rowKey)
  // `== null` rather than `=== undefined`, so a caller whose `renderRowHeader` returns
  // `null` for one row does not end up with some swimlanes named and others not.
  const hasHeader = header != null
  const framing = hasHeader ? rowProps : {}

  return (
    <div {...framing} className={clsx(styles.RowFrame, className)}>
      {hasHeader ? <div className={styles.Header}>{header}</div> : null}
      <div className={styles.Cells}>{children}</div>
    </div>
  )
}
